import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Spin, message, Button } from "antd";
import { gql, useLazyQuery } from "@apollo/client";
import { QRCodeSVG } from "qrcode.react";
import ChallengePreview from "../../../components/ChallengePreview";
import { SharePageContainer, QRCodeBox } from "./style";

const CHALLENGE_FOR_PUBLIC_BRANDING = gql`
  query challengeForPublicBranding($_id: String!) {
    challengeForPublicBranding(_id: $_id) {
      _id
      banner
      title
      description
      enrolledCount
      subscriberCount
      publishedAt
      scheduledAt
      coach {
        _id
        name
        profile_image
        socialLink
        about
      }
    }
  }
`;

export default function ShareChallenge() {
  const [searchParams] = useSearchParams();
  const challengeId = searchParams.get("challengeId");
  const [challengeData, setChallengeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const qrCodeRef = useRef(null);
  const [getChallenge, { data, loading: queryLoading }] = useLazyQuery(
    CHALLENGE_FOR_PUBLIC_BRANDING
  );

  useEffect(() => {
    // 移除 body 的 padding，因为分享页面应该是全屏的
    const computedStyle = window.getComputedStyle(document.body);
    const originalPaddingTop = computedStyle.paddingTop;
    const originalPaddingLeft = computedStyle.paddingLeft;
    const originalTitle = document.title;

    document.body.style.paddingTop = "0";
    document.body.style.paddingLeft = "0";

    // 清理函数：组件卸载时恢复原来的 padding、title 和 meta tags
    return () => {
      document.body.style.paddingTop = originalPaddingTop;
      document.body.style.paddingLeft = originalPaddingLeft;
      document.title = originalTitle;
    };
  }, []);

  useEffect(() => {
    if (challengeId) {
      setLoading(true);
      getChallenge({
        variables: {
          _id: challengeId,
        },
      });
    } else {
      message.error("Challenge ID is required");
    }
  }, [challengeId, getChallenge]);

  useEffect(() => {
    if (data?.challengeForPublicBranding) {
      setChallengeData(data.challengeForPublicBranding);
      setLoading(false);
    }
  }, [data]);

  useEffect(() => {
    if (queryLoading !== undefined) {
      setLoading(queryLoading);
    }
  }, [queryLoading]);

  // Current page URL for QR code and sharing
  const currentPageUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (challengeId) params.set("challengeId", challengeId);
    return `${window.location.origin}/ShareChallenge?${params.toString()}`;
  }, [challengeId]);

  // Deep link URL for React Native app
  // Format: myapp://challenge/:challengeId
  // You can customize this based on your app's deep link scheme
  const deepLink = `uvitacoach://challenge/${challengeId}`;

  const handleOpenInApp = () => {
    if (!challengeId) {
      message.error("Challenge ID is missing");
      return;
    }

    // Detect if we're on iOS or Android
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);

    // Universal link for iOS (if configured)
    // const universalLink = `https://yourdomain.com/challenge/${challengeId}`;

    // App store links (replace with your actual app IDs)
    const iosAppStoreLink = "https://apps.apple.com/app/1561648928";
    const androidPlayStoreLink =
      "https://play.google.com/store/apps/details?id=com.uvitacoach";
    // 复制deeplink到剪贴板
    navigator.clipboard.writeText(deepLink);
    message.success("Link copied to clipboard");

    // Try to open the app using deep link
    if (isIOS) {
      // For iOS, try universal link first, then fallback to custom scheme
      // window.location.href = universalLink;
      window.location.href = deepLink;
      // 复制deeplink到剪贴板

      // Fallback to App Store after a delay if app doesn't open
      setTimeout(() => {
        if (document.hasFocus()) {
          // App didn't open, redirect to App Store
          window.location.href = iosAppStoreLink;
        }
      }, 2500);
    } else if (isAndroid) {
      // For Android, use intent URL for better compatibility
      const intentLink = `intent://challenge/${challengeId}#Intent;scheme=uvitacoach;package=com.uvitacoach;end`;

      // Try intent link first
      window.location.href = intentLink;

      // Fallback to custom scheme
      setTimeout(() => {
        if (document.hasFocus()) {
          window.location.href = deepLink;
        }
      }, 500);

      // Final fallback to Play Store
      setTimeout(() => {
        if (document.hasFocus()) {
          window.location.href = androidPlayStoreLink;
        }
      }, 2500);
    } else {
      // Desktop or other platforms - just try the deep link
      window.location.href = deepLink;
    }
  };

  const handleClickCoach = () => {
    console.log("click coach");
    // 使用 window.postMessage 将消息传到 app
    if (window.ReactNativeWebView) {
      // React Native WebView
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "SUBSCRIBER_LIST",
          data: {
            coachUserId: challengeData.coach._id,
          },
        })
      );
    } else if (window.parent) {
      // 通用 postMessage
      window.parent.postMessage(
        {
          type: "SUBSCRIBER_LIST",
          data: {
            coachUserId: challengeData.coach._id,
          },
        },
        "*"
      );
    }
  };

  const handleDownloadQRCode = () => {
    try {
      const svg = qrCodeRef.current;
      if (!svg) {
        message.error("QR Code not found");
        return;
      }

      // 创建 canvas
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const svgData = new XMLSerializer().serializeToString(svg);
      const img = new Image();

      // 设置 canvas 尺寸
      canvas.width = 100;
      canvas.height = 100;

      img.onload = () => {
        // 绘制白色背景
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        // 绘制 QR Code
        ctx.drawImage(img, 0, 0);

        // 下载
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `challenge-qr-${challengeId}.png`;
          link.click();
          URL.revokeObjectURL(url);
          message.success("QR Code downloaded successfully");
        });
      };

      img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    } catch (error) {
      console.error("Error downloading QR Code:", error);
      message.error("Failed to download QR Code");
    }
  };

  if (loading) {
    return (
      <SharePageContainer>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
          }}
        >
          <Spin size="large" />
        </div>
      </SharePageContainer>
    );
  }

  if (!challengeData) {
    return (
      <SharePageContainer>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
          }}
        >
          <div>Challenge not found</div>
        </div>
      </SharePageContainer>
    );
  }

  return (
    <SharePageContainer>
      <div className="share-page-wrapper">
        <ChallengePreview
          challengeData={challengeData}
          onOpenInApp={handleOpenInApp}
          onClickCoach={handleClickCoach}
        />
      </div>
      <QRCodeBox>
        <div className="qr-text">
          <span>Open in the App by</span>
          <span>scanning the QR code</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <QRCodeSVG ref={qrCodeRef} value={currentPageUrl} size={100} />
          <Button
            type="link"
            size="small"
            onClick={handleDownloadQRCode}
            style={{
              color: "#ffffff",
              padding: "4px 0 0 0",
              height: "auto",
              fontSize: "12px",
            }}
          >
            Download
          </Button>
        </div>
      </QRCodeBox>
    </SharePageContainer>
  );
}
